/**
 * Calls backend Salesforce token APIs (exchange + refresh).
 * Uses credentials: 'include' so HttpOnly cookies set by the backend are sent and stored.
 * Returns token set from response; backend also sets sf_* cookies (HttpOnly).
 */

import { getEnv } from "../../config/getEnv";

export interface SfTokenSet {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  instance_url?: string;
  expires_in?: number;
  issued_at?: number;
}

export interface ExchangeTokenParams {
  code: string;
  code_verifier: string;
  redirect_uri: string;
  auth_domain: string;
  client_id: string;
}

export interface RefreshTokenParams {
  refresh_token?: string;
  auth_domain: string;
  client_id: string;
}

const BE_URL = getEnv("REACT_APP_BE_URL") || "http://localhost:8080";

function getAuthBaseUrl(): string {
  return BE_URL.replace(/\/$/, "");
}

export async function exchangeTokenViaBackend(
  params: ExchangeTokenParams,
): Promise<SfTokenSet> {
  const { code, code_verifier, redirect_uri, auth_domain, client_id } = params;
  const url = `${getAuthBaseUrl()}/api/auth/sf/token`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    mode: "cors",
    body: JSON.stringify({
      code,
      code_verifier,
      redirect_uri,
      auth_domain,
      client_id,
    }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as {
      error?: { message?: string };
      message?: string;
    };
    throw new Error(
      err?.error?.message ||
        err?.message ||
        `Token exchange failed (${res.status})`,
    );
  }
  const json = (await res.json()) as { data?: SfTokenSet } & SfTokenSet;
  const data = json?.data ?? json;
  return {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    id_token: data.id_token,
    instance_url: data.instance_url,
    expires_in: data.expires_in,
    issued_at: data.issued_at ?? Date.now(),
  };
}

export async function refreshTokenViaBackend(
  params: RefreshTokenParams,
): Promise<SfTokenSet> {
  const { refresh_token, auth_domain, client_id } = params;
  const url = `${getAuthBaseUrl()}/api/auth/sf/refresh`;
  const body: Record<string, string> = {};
  if (refresh_token) body.refresh_token = refresh_token;
  if (auth_domain) body.auth_domain = auth_domain;
  if (client_id) body.client_id = client_id;

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
    mode: "cors",
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as {
      error?: { message?: string };
      message?: string;
    };
    throw new Error(
      err?.error?.message ||
        err?.message ||
        `Token refresh failed (${res.status})`,
    );
  }
  const json = (await res.json()) as { data?: SfTokenSet } & SfTokenSet;
  const data = json?.data ?? json;
  return {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    id_token: data.id_token,
    instance_url: data.instance_url,
    expires_in: data.expires_in,
    issued_at: data.issued_at ?? Date.now(),
  };
}

export async function clearSfCookiesViaBackend(): Promise<void> {
  const url = `${getAuthBaseUrl()}/api/auth/sf/logout`;
  await fetch(url, {
    method: "POST",
    credentials: "include",
    mode: "cors",
  });
}
